import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../api/axiosInstance";
import AuthLayout, { AuthAlert } from "../components/AuthLayout";
import ResendVerification from "../components/ResendVerification";

// The user lands here from the link in the verification email
// (/verify-email/:token). The page sends the token to the backend straight
// away and shows the result: verified, already verified, or invalid/expired.
export default function VerifyEmail() {
  const { token } = useParams();

  const [state, setState] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState("");

  // React StrictMode runs effects twice in development; this makes sure the
  // request is only sent once. (The backend also treats a repeat as "already
  // verified", so a double call would be harmless anyway.)
  const sentRef = useRef(false);

  useEffect(() => {
    if (sentRef.current) return;
    sentRef.current = true;

    api
      .post(`/user/verify-email/${token}`)
      .then((res) => {
        setState("success");
        setMessage(res.data.message);
      })
      .catch((err) => {
        setState("error");
        setMessage(err.response?.data?.message || "We couldn't verify your email. Please try again.");
      });
  }, [token]);

  if (state === "loading") {
    return (
      <AuthLayout title="Verifying your email" subtitle="Just a moment...">
        <p className="au-hint">Checking your verification link.</p>
      </AuthLayout>
    );
  }

  if (state === "success") {
    return (
      <AuthLayout
        title="Email verified"
        subtitle="Your account is active. Sign in to get started."
        footer={<Link to="/login">Go to sign in</Link>}
      >
        <AuthAlert tone="success">{message}</AuthAlert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Verification failed"
      subtitle="Enter your email below and we'll send you a new verification link."
      footer={
        <>
          Already verified? <Link to="/login">Sign in</Link>
        </>
      }
    >
      <AuthAlert tone="error">{message}</AuthAlert>
      <ResendVerification label="Send a new verification link" />
    </AuthLayout>
  );
}